import { useEffect, useState } from 'react'
import { Button, Card, EmptyState, ExplainerBanner, Icon, Skeleton, SuccessBlock } from '@/ds'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { listJoin } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { extrasService } from '@/services/extras'
import { SectionError } from '../Estancia/sections/SectionError'
import { PAY_ROUTES } from '../Pagos/shared'
import { useLang } from '../Reserva/shared'
import { amountText, EXTRAS_ROUTES, namesOf, orderTitle, readExtrasResult, writeCart } from './shared'
import type { ExtrasResult } from './shared'

/**
 * 8.6 · Extras confirmados (Servicios.dc.html cartDone, Bloque 6 §6c). Mock: cada extra con su información
 * práctica y los recibos. API actual (D46): lo añadido a la reserva, cómo se cobra, y lo que no se pudo añadir.
 */
export function ExtrasListo() {
  const { t } = useLang()
  const stay = useLoadedStay()
  const { go } = useAppNavigate()
  const [result] = useState(() => readExtrasResult(stay.guestCode))
  return (
    <main className="page">
      <div className="pagos__head">
        <h1>{t('v2.extras.listo.titulo')}</h1>
        {result?.mode === 'paid' && <PaidLead checkoutId={result.checkoutId} />}
      </div>
      {!result && (
        <EmptyState icon="shopping-bag" title={t('v2.extras.listo.sinResumen')} headingLevel={2} actionLabel={t('v2.extras.confirmar.verExtras')} onAction={() => go(EXTRAS_ROUTES.catalog, { replace: true })}>
          {t('v2.extras.listo.sinResumenTexto')}
        </EmptyState>
      )}
      {result?.mode === 'paid' && <Paid checkoutId={result.checkoutId} />}
      {result?.mode === 'added' && <Added result={result} />}
      <Button onClick={() => go('/')}>{t('v2.extras.listo.volverEstancia')}</Button>
    </main>
  )
}

function useCheckout(checkoutId: string) {
  const stay = useLoadedStay()
  return useAsync(`extras-checkout:${stay.guestCode}:${checkoutId}`, (signal) => extrasService.getCheckout(stay, checkoutId, signal))
}

function PaidLead({ checkoutId }: { checkoutId: string }) {
  const { t } = useLang()
  const [data] = useCheckout(checkoutId)
  const email = asyncValue(data)?.receiptsEmail
  return email ? <p className="page__lead">{t('v2.extras.listo.lead', { email })}</p> : null
}

function Paid({ checkoutId }: { checkoutId: string }) {
  const { t, lang } = useLang()
  const { go } = useAppNavigate()
  const stay = useLoadedStay()
  const [data, reload] = useCheckout(checkoutId)
  // Cobrado: el carrito ya está contratado.
  useEffect(() => {
    if (data.kind === 'ready') writeCart(stay.guestCode, [])
  }, [data.kind, stay.guestCode])
  if (data.kind === 'loading')
    return (
      <div aria-busy="true">
        <Skeleton height={260} />
      </div>
    )
  if (data.kind === 'error') return <SectionError code={data.code} onRetry={reload} />
  if (data.kind !== 'ready')
    return (
      <EmptyState icon="shopping-bag" title={t('v2.extras.listo.sinResumen')} headingLevel={2}>
        {t('v2.extras.listo.sinResumenTexto')}
      </EmptyState>
    )
  const { orders } = data.value
  return (
    <>
      <SuccessBlock title={t('v2.extras.listo.confirmados')} headingLevel={2} />
      <Card>
        <ul className="extras__orders">
          {orders.map((o) => (
            <li key={o.id} className="extras__order">
              <span>
                <span className="extras__order-title">{orderTitle(o, lang)}</span>
                {o.practicalInfo && <span className="extras__order-info">{o.practicalInfo}</span>}
              </span>
              <span className={o.amount.amount === 0 ? 'extras__free tnum' : 'tnum'}>{amountText(o.amount, lang, t)}</span>
            </li>
          ))}
        </ul>
      </Card>
      <button type="button" className="pagos__chevron-link" onClick={() => go(PAY_ROUTES.receipts)}>
        {t('v2.extras.listo.verRecibos')}
        <Icon name="chevron-right" size={20} />
      </button>
    </>
  )
}

function Added({ result }: { result: Extract<ExtrasResult, { mode: 'added' }> }) {
  const { t, lang } = useLang()
  const stay = useLoadedStay()
  const { go } = useAppNavigate()
  // Los nombres se leen del catálogo (E9); si no carga, se muestran los identificadores.
  const [data] = useAsync(`extras:${stay.guestCode}`, (signal) => extrasService.list(stay, signal))
  const extras = asyncValue(data) ?? []
  const added = namesOf(result.added, extras)
  return (
    <>
      <SuccessBlock title={t('v2.extras.listo.tituloReal')} headingLevel={2}>
        {t('v2.legacy.preview.labels.availableExtrasView.infoMessage1')}
      </SuccessBlock>
      <Card>
        <ul className="extras__orders">
          {added.map((name, i) => (
            <li key={`${name}-${i}`} className="extras__order">
              <span className="extras__order-title">{name}</span>
            </li>
          ))}
        </ul>
      </Card>
      {result.failed && (
        <ExplainerBanner tone="warning" title={t('v2.legacy.preview.labels.availableExtrasView.errorTitle')}>
          {t('v2.extras.confirmar.parcial', { count: added.length, added: listJoin(added, lang), failed: namesOf([result.failed], extras)[0] })}{' '}
          {result.notTried.length > 0 && t('v2.extras.confirmar.noIntentados', { count: result.notTried.length, names: listJoin(namesOf(result.notTried, extras), lang) })}
        </ExplainerBanner>
      )}
      {result.failed && (
        <Button variant="secondary" onClick={() => go(EXTRAS_ROUTES.confirm)}>
          {t('v2.comun.reintentar')}
        </Button>
      )}
      <p className="pagos__note">{t('v2.legacy.preview.labels.availableExtrasView.infoMessage2')}</p>
    </>
  )
}
